/**
 * ResolveDiscrepancyModal — shared single-discrepancy resolve dialog.
 * Used by Discrepancies queue (single + bulk via summary item shape) and
 * DiscrepancyDetail page. Extracted from Discrepancies.tsx so the queue
 * and detail surface stay in lockstep.
 *
 * Resolution badge + condition badge tables are exported alongside so
 * cells in either surface render the same colors / labels.
 */

import { useState, useEffect, type JSX } from 'react';
import { X } from 'lucide-react';
import ArbiSelect from '../ui/ArbiSelect';

type ResolutionValue =
  | 'accept_shortage'
  | 'request_replacement'
  | 'return_to_distributor'
  | 'write_off';

const RESOLUTION_OPTIONS: { value: ResolutionValue; label: string }[] = [
  { value: 'accept_shortage', label: 'Accept Shortage' },
  { value: 'request_replacement', label: 'Request Replacement' },
  { value: 'return_to_distributor', label: 'Return to Distributor' },
  { value: 'write_off', label: 'Write Off' },
];

export interface BadgeStyle {
  label: string;
  cls: string;
}

export const RESOLUTION_BADGES: Record<string, BadgeStyle> = {
  accept_shortage: { label: 'Accepted', cls: 'bg-surface-4 text-ink-muted' },
  request_replacement: { label: 'Replacement', cls: 'bg-info/10 text-info' },
  return_to_distributor: { label: 'Return', cls: 'bg-warning/10 text-warning' },
  write_off: { label: 'Written Off', cls: 'bg-surface-4 text-ink-muted' },
};

export const CONDITION_BADGES: Record<string, BadgeStyle> = {
  new: { label: 'New', cls: 'bg-success/10 text-success' },
  damaged: { label: 'Damaged', cls: 'bg-warning/10 text-warning' },
  wrong_item: { label: 'Wrong Item', cls: 'bg-danger/10 text-danger' },
  missing: { label: 'Missing', cls: 'bg-danger/10 text-danger' },
};

export interface ResolveItemSummary {
  isg_sku: string | null;
  expected_qty: number | string;
  received_qty: number | string;
  condition: string;
}

interface ResolveDiscrepancyModalProps {
  open: boolean;
  item: ResolveItemSummary | null;
  onConfirm: (resolution: string, notes: string) => void;
  onCancel: () => void;
  submitting: boolean;
}

export default function ResolveDiscrepancyModal({
  open,
  item,
  onConfirm,
  onCancel,
  submitting,
}: ResolveDiscrepancyModalProps): JSX.Element | null {
  const [resolution, setResolution] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    if (open) {
      setResolution('');
      setNotes('');
    }
  }, [open]);

  if (!open || !item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 cursor-default"
        onClick={onCancel}
        aria-label="Close dialog"
        tabIndex={-1}
      />
      <div
        className="relative bg-surface-2 border border-border rounded-xl w-[420px] shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Resolve Discrepancy"
      >
        <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink-bright">
            Resolve Discrepancy — {item.isg_sku}
          </h3>
          <button
            onClick={onCancel}
            className="text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="bg-surface-3 rounded-lg p-3 space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">Expected Qty</span>
              <span className="font-mono text-ink-bright">{item.expected_qty}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">Received Qty</span>
              <span className="font-mono text-ink-bright">{item.received_qty}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-ink-muted">Condition</span>
              <span className="text-ink-bright">
                {CONDITION_BADGES[item.condition]?.label || item.condition}
              </span>
            </div>
          </div>

          <div>
            <label
              htmlFor="discrepancy-resolution"
              className="text-xs font-medium text-ink-secondary mb-1 block"
            >
              Resolution
            </label>
            <div id="discrepancy-resolution">
              <ArbiSelect
                value={resolution}
                onChange={(v) => setResolution(v)}
                options={RESOLUTION_OPTIONS}
                placeholder="Select resolution…"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="discrepancy-notes"
              className="text-xs font-medium text-ink-secondary mb-1 block"
            >
              Notes (optional)
            </label>
            <textarea
              id="discrepancy-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="arbi-input w-full text-xs resize-none min-h-[80px]"
              placeholder="Additional notes…"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-border-subtle flex justify-end gap-2">
          <button onClick={onCancel} className="arbi-btn-ghost text-xs" disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={() => onConfirm(resolution, notes)}
            className="arbi-btn-primary text-xs"
            disabled={submitting || !resolution}
          >
            {submitting ? 'Saving…' : 'Save Resolution'}
          </button>
        </div>
      </div>
    </div>
  );
}
